import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../services/api";
import PublicNavbar from "../components/public/PublicNavbar";
import PublicFooter from "../components/public/PublicFooter";
import ProductCard from "../components/product/ProductCard";
import PurchaseModal from "../components/public/PurchaseModal";
import { FaSearch, FaTimes } from "react-icons/fa";
import { DEFAULT_LUMIGO_PRODUCTS } from "../data/defaultProducts";

const CATEGORY_TABS = [
    { label: "All Products", value: "ALL" },
    { label: "Outdoor Projectors", value: "Outdoor" },
    { label: "Indoor Projectors", value: "Indoor" },
    { label: "High Power Facade", value: "High Power" },
    { label: "Dynamic & Effects", value: "Dynamic" },
    { label: "Custom Gobo Glass", value: "Custom" },
];

const Products = () => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const categoryParam = queryParams.get("category");
    const searchParam = queryParams.get("search");

    const [allProducts, setAllProducts] = useState(DEFAULT_LUMIGO_PRODUCTS);
    const [filteredProducts, setFilteredProducts] = useState(DEFAULT_LUMIGO_PRODUCTS);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("ALL");
    const [searchQuery, setSearchQuery] = useState(searchParam || "");
    const [showInquiryModal, setShowInquiryModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Fetch Products from API (with fallback to DEFAULT_LUMIGO_PRODUCTS)
    useEffect(() => {
        let isMounted = true;
        const fetchProducts = async () => {
            try {
                const { data } = await api.get("/api/products");
                if (isMounted) {
                    if (Array.isArray(data) && data.length > 0) {
                        setAllProducts(data);
                    } else {
                        setAllProducts(DEFAULT_LUMIGO_PRODUCTS);
                    }
                }
            } catch (error) {
                console.warn("Failed to fetch products from backend, using default catalogue:", error);
                if (isMounted) {
                    setAllProducts(DEFAULT_LUMIGO_PRODUCTS);
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchProducts();
        return () => { isMounted = false; };
    }, []);

    // Sync with URL query
    useEffect(() => {
        if (categoryParam) {
            const found = CATEGORY_TABS.find(t => t.value.toLowerCase() === categoryParam.toLowerCase());
            if (found) setActiveTab(found.value);
        }
        if (searchParam) {
            setSearchQuery(searchParam);
        }
    }, [categoryParam, searchParam]);

    // Filter Logic
    useEffect(() => {
        let result = allProducts;

        // Search text
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter(p =>
                p.name?.toLowerCase().includes(q) ||
                p.modelNumber?.toLowerCase().includes(q) ||
                p.sku?.toLowerCase().includes(q) ||
                p.category?.toLowerCase().includes(q) ||
                p.description?.toLowerCase().includes(q)
            );
        }

        // Category Tab
        if (activeTab !== "ALL") {
            const lowerTab = activeTab.toLowerCase();
            result = result.filter(p => {
                const cat = (p.category || "").toLowerCase();
                const name = (p.name || "").toLowerCase();
                if (lowerTab === "outdoor") return cat.includes("outdoor") || name.includes("outdoor") || name.includes("dxax");
                if (lowerTab === "indoor") return cat.includes("indoor") || name.includes("indoor") || name.includes("dxin");
                if (lowerTab === "high power") return cat.includes("high") || name.includes("200w") || name.includes("300w") || name.includes("ultra") || cat.includes("architectural");
                if (lowerTab === "dynamic") return cat.includes("dynamic") || cat.includes("effect") || name.includes("water") || name.includes("rot") || cat.includes("festive") || name.includes("rangoli");
                if (lowerTab === "custom") return cat.includes("custom") || cat.includes("gobo") || name.includes("gobo") || name.includes("lens");
                return cat.includes(lowerTab);
            });
        }

        setFilteredProducts(result);
    }, [allProducts, activeTab, searchQuery]);

    const handleOpenEnquiry = (product) => {
        setSelectedProduct(product);
        setShowInquiryModal(true);
    };

    return (
        <div className="min-h-screen bg-white text-[#263746] font-sans antialiased selection:bg-[#E3B629] selection:text-[#263746]">
            <PublicNavbar onOpenEnquiry={() => handleOpenEnquiry(null)} />

            {/* 1. ARCHITECTURAL BANNER HEADER */}
            <div
                className="relative bg-[#1A232E] pt-28 pb-16 px-4 sm:px-6 lg:px-8 text-center bg-cover bg-center overflow-hidden"
                style={{
                    backgroundImage: `url('/assets/lumigo/projector-3.jpg')`,
                    backgroundPosition: "center 30%",
                }}
            >
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-[#0F1722]/85" />

                <div className="relative z-10 max-w-4xl mx-auto">
                    <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
                        Our Products
                    </h1>
                    <p className="text-gray-300 text-xs sm:text-sm mt-3 max-w-xl mx-auto font-normal">
                        Explore our range of commercial gobo projectors for every application.
                    </p>
                </div>
            </div>

            {/* 2. CATEGORY PILLS & SEARCH BAR (Matches Panel 2 of Image 1) */}
            <div className="border-b border-[#E5E7EB] bg-white sticky top-20 z-30 shadow-xs">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

                        {/* Category Pills */}
                        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 custom-scrollbar">
                            {CATEGORY_TABS.map(tab => (
                                <button
                                    key={tab.value}
                                    onClick={() => setActiveTab(tab.value)}
                                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap ${activeTab === tab.value
                                        ? "bg-[#E3B629] text-[#263746] shadow-sm"
                                        : "bg-[#F7F5EF] text-[#687482] hover:text-[#263746] hover:bg-[#EAE7DD]"
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Search Input Box */}
                        <div className="relative w-full md:w-64 flex-shrink-0">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search products..."
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-full pl-9 pr-8 py-2 text-xs text-[#263746] placeholder-[#687482] focus:outline-none focus:border-[#E3B629] focus:bg-white transition"
                            />
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#687482] text-xs pointer-events-none" />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#687482] hover:text-[#263746]"
                                >
                                    <FaTimes className="text-xs" />
                                </button>
                            )}
                        </div>

                    </div>
                </div>
            </div>

            {/* 3. PRODUCT GRID */}
            <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-center justify-between mb-6 text-xs text-[#687482]">
                    <span>Showing <strong className="text-[#263746]">{filteredProducts.length}</strong> commercial models</span>
                    {activeTab !== "ALL" && (
                        <button
                            onClick={() => { setActiveTab("ALL"); setSearchQuery(""); }}
                            className="text-[#E3B629] hover:underline font-bold"
                        >
                            Reset Filter
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="py-24 text-center">
                        <div className="w-8 h-8 border-2 border-[#E3B629] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                        <span className="text-xs text-[#687482]">Loading LUMIGO catalogue...</span>
                    </div>
                ) : filteredProducts.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredProducts.map((product) => (
                            <ProductCard
                                key={product._id}
                                product={product}
                                onEnquire={handleOpenEnquiry}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-24 bg-[#F7F5EF] rounded-2xl border border-dashed border-[#E5E7EB] p-8">
                        <h3 className="text-base font-bold text-[#263746] mb-1">No Matching Models Found</h3>
                        <p className="text-xs text-[#687482] max-w-sm mx-auto mb-4">
                            Try searching with another keyword or resetting the category filter.
                        </p>
                        <button
                            onClick={() => { setActiveTab("ALL"); setSearchQuery(""); }}
                            className="bg-[#E3B629] text-[#263746] font-bold text-xs px-5 py-2 rounded-lg"
                        >
                            Show All Products
                        </button>
                    </div>
                )}

                {/* Pagination matching Image 1 */}
                {filteredProducts.length > 0 && (
                    <div className="flex items-center justify-center gap-2 mt-12 pt-8 border-t border-[#E5E7EB]">
                        <button className="w-8 h-8 rounded-lg bg-[#E3B629] text-[#263746] font-bold text-xs flex items-center justify-center shadow-xs">
                            1
                        </button>
                        <button className="w-8 h-8 rounded-lg bg-[#F7F5EF] text-[#687482] hover:bg-[#EAE7DD] font-bold text-xs flex items-center justify-center transition">
                            2
                        </button>
                        <button className="w-8 h-8 rounded-lg bg-[#F7F5EF] text-[#687482] hover:bg-[#EAE7DD] font-bold text-xs flex items-center justify-center transition">
                            3
                        </button>
                        <span className="text-xs text-[#687482] px-1">...</span>
                    </div>
                )}
            </main>

            {/* Footer */}
            <PublicFooter />

            {/* Direct Purchase & Client Account Setup Modal */}
            {showInquiryModal && (
                <PurchaseModal
                    product={selectedProduct}
                    onClose={() => {
                        setShowInquiryModal(false);
                        setSelectedProduct(null);
                    }}
                />
            )}
        </div>
    );
};

export default Products;
